/**
 * @fileoverview Avatar Companion Type Definitions
 * @module shared/types/AvatarCompanion
 * @version 1.0.0
 * @author Telegamez Avatar System
 * 
 * @description
 * Comprehensive TypeScript type definitions for the Avatar Companion feature.
 * Includes database entity types, API request/response types, and validation schemas
 * using Zod for runtime type safety. These types are shared across the entire
 * monorepo to ensure consistency between database schema, API endpoints, and frontend.
 * 
 * @dependencies
 * - zod: Runtime schema validation
 * - Database schema compatibility with packages/shared/db/src/schema.ts
 * - API endpoint compatibility with packages/web-next/src/app/api/avatar-companions/
 * 
 * @type_coverage
 * - Database entity types (AvatarCompanion, NewAvatarCompanion)
 * - API response types (AvatarCompanionAPI, AvatarCompanionsSuccessResponse, etc.)
 * - Request/response validation schemas
 * - Enum types for categories and status values
 * - Workflow integration types for avatar assignment
 */

import { z } from 'zod';
import { CharacterRuntimeReleaseSchema } from './AvatarManifest';
import { CharacterUnavailabilitySchema } from './CharacterAvailability';

// =============================================================================
// ENUMS AND CONSTANTS
// =============================================================================

/**
 * Avatar Companion Categories
 * 
 * Defines the available categories for avatar companions to enable
 * filtering and organization in the UI.
 */
export const AvatarCompanionCategory = {
  /** Gaming and esports focused companions */
  GAMING: 'gaming',
  /** Educational and learning focused companions */
  EDUCATION: 'education',
  /** Entertainment and media focused companions */
  ENTERTAINMENT: 'entertainment',
  /** Business and professional focused companions */
  BUSINESS: 'business',
  /** Technology and development focused companions */
  TECHNOLOGY: 'technology',
  /** General purpose companions */
  GENERAL: 'general',
  /** Health and fitness focused companions */
  HEALTH: 'health',
  /** News and journalism focused companions */
  NEWS: 'news',
  /** Sports and athletics focused companions */
  SPORTS: 'sports',
  /** Wellness and mindfulness focused companions */
  WELLNESS: 'wellness'
} as const;

/**
 * The set of valid companion categories, as a type.
 *
 * Deliberately a union of string literals rather than a TypeScript `enum`.
 * A string enum is nominal, so `category: 'general'` would not typecheck
 * against it and every call site would need `AvatarCompanionCategory.GENERAL`
 * — friction that historically got resolved with `string`, which is how an
 * empty category reached the database in the first place. With a literal union
 * a valid category assigns freely and an invalid one is a compile error.
 */
export type AvatarCompanionCategory =
  (typeof AvatarCompanionCategory)[keyof typeof AvatarCompanionCategory];

/**
 * Valid avatar companion categories as array for validation
 */
export const AVATAR_COMPANION_CATEGORIES = Object.values(AvatarCompanionCategory);

// =============================================================================
// VALIDATION SCHEMAS
// =============================================================================

/**
 * Base avatar companion validation schema
 * 
 * Defines the structure and validation rules for avatar companion data
 * as stored in the database. Used for both insert and select operations.
 */
export const AvatarCompanionSchema = z.object({
  /** Unique identifier (UUID) */
  id: z.string().uuid(),
  
  /** Display name for the avatar companion */
  name: z.string().min(1).max(255),
  
  /** URL-friendly slug for the avatar companion */
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/),
  
  /** Detailed description of personality and expertise */
  description: z.string().nullable(),
  
  /** Path to the 3D model file (.glb) */
  modelPath: z.string().min(1).max(500),

  /** Admin-set room PIP framing, resolved from the avatar (see `avatars.metadata.framing`). */
  framing: z.object({ fov: z.number(), heightOffset: z.number() }).nullable().optional(),
  /** The live version this body serves (#4632 phase 2), resolved with the model. */
  characterRelease: CharacterRuntimeReleaseSchema.nullable().optional(),
  /** Set only when the character was withdrawn and its assignments marked unavailable. */
  characterAvailability: CharacterUnavailabilitySchema.nullable().optional(),
  
  /** Cartesia voice ID for TTS */
  voiceId: z.string().min(1).max(255),
  
  /** Optional URL to video preview MP4 file (contains both video and audio) */
  videoPreviewUrl: z.string().max(500).nullable(),
  
  /** Keywords used in LLM prompts for personality */
  promptKeywords: z.array(z.string()).nullable(),
  
  /** Category classification */
  category: z.nativeEnum(AvatarCompanionCategory),
  
  /** Tags for additional filtering and search */
  tags: z.array(z.string()).nullable(),
  
  /** Whether the companion is available for selection */
  isActive: z.boolean(),
  /**
   * #3672 — the platform default companion. A READ field only: it is
   * deliberately absent from `AvatarCompanionWriteSchema`'s pick list, because
   * it is a platform singleton guarded by a partial unique index rather than a
   * companion attribute. Promotion goes through `CompanionService.update`,
   * which clears the incumbent in the same transaction.
   */
  isDefault: z.boolean().optional(),

  /** Custom system prompt override (bypasses template generation if set) */
  customPrompt: z.string().nullable().optional(),
  /** #3666 — room mode entered on companion attach. Null = declares none. */
  defaultModeId: z.string().nullable().optional(),
  /** #3685 — skills enabled in a room on attach. Null = declares none. */
  skillIds: z.array(z.string()).nullable().optional(),

  /** Optional per-companion LLM configuration */
  brainConfig: z.record(z.any()).optional().nullable(),

  /** Record creation timestamp */
  createdAt: z.date(),
  
  /** Record last update timestamp */
  updatedAt: z.date()
});

/**
 * Schema for creating new avatar companions
 * 
 * Omits auto-generated fields (id, timestamps) for insert operations
 */
export const NewAvatarCompanionSchema = AvatarCompanionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

/**
 * Schema for updating existing avatar companions
 * 
 * Makes most fields optional for partial updates
 */
export const UpdateAvatarCompanionSchema = AvatarCompanionSchema.partial().omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

/**
 * Fields an admin write (create) may set on an avatar companion.
 *
 * Derived from `AvatarCompanionSchema` deliberately: the write boundary must
 * accept exactly what the read boundary is able to parse. These were
 * historically independent — the read path enforced the category enum while
 * every write path treated `category` as a free string — so a companion could
 * be created that the catalog API could never return. Because
 * `AvatarCompanionsSuccessResponseSchema.data` is a `z.array` (all-or-nothing),
 * a single such row emptied the entire companion list for every host.
 *
 * Required fields mirror the NOT NULL columns on `avatar_companions`; the
 * nullable columns are optional here and may be omitted entirely.
 */
export const AvatarCompanionWriteSchema = AvatarCompanionSchema
  .pick({
    name: true,
    slug: true,
    description: true,
    modelPath: true,
    voiceId: true,
    videoPreviewUrl: true,
    promptKeywords: true,
    category: true,
    tags: true
  })
  .partial({
    description: true,
    videoPreviewUrl: true,
    promptKeywords: true,
    tags: true
  });

/**
 * Fields an admin write (update) may set on an avatar companion.
 *
 * Same contract as {@link AvatarCompanionWriteSchema}, with every field
 * optional for partial updates. A field that IS supplied must still satisfy
 * the read contract.
 */
export const AvatarCompanionUpdateWriteSchema = AvatarCompanionWriteSchema.partial();

/** Admin write payload for creating an avatar companion */
export type AvatarCompanionWrite = z.infer<typeof AvatarCompanionWriteSchema>;

/** Admin write payload for updating an avatar companion */
export type AvatarCompanionUpdateWrite = z.infer<typeof AvatarCompanionUpdateWriteSchema>;

/**
 * Schema for avatar companion category filtering
 */
export const AvatarCompanionCategorySchema = z.nativeEnum(AvatarCompanionCategory);

// =============================================================================
// DATABASE ENTITY TYPES
// =============================================================================

/**
 * Complete avatar companion entity as stored in database
 * 
 * Represents the full avatar companion record with all fields including
 * auto-generated timestamps and UUID. Used when retrieving companions
 * from the database.
 */
export type AvatarCompanion = z.infer<typeof AvatarCompanionSchema>;

/**
 * New avatar companion data for creation
 * 
 * Used when inserting new avatar companions into the database.
 * Excludes auto-generated fields (id, timestamps).
 */
export type NewAvatarCompanion = z.infer<typeof NewAvatarCompanionSchema>;

/**
 * Partial avatar companion data for updates
 * 
 * Used when updating existing avatar companions. All fields except
 * id and timestamps are optional to support partial updates.
 */
export type UpdateAvatarCompanion = z.infer<typeof UpdateAvatarCompanionSchema>;

/**
 * Avatar companion category type
 */
export type AvatarCompanionCategoryType = z.infer<typeof AvatarCompanionCategorySchema>;

// =============================================================================
// API REQUEST/RESPONSE SCHEMAS
// =============================================================================

/**
 * Schema for GET /api/avatar-companions query parameters
 */
export const GetAvatarCompanionsQuerySchema = z.object({
  /** Optional category filter */
  category: AvatarCompanionCategorySchema.optional()
});

/**
 * Schema for avatar companion as returned by API (with string dates)
 */
export const AvatarCompanionAPISchema = z.object({
  /** Unique identifier (UUID) */
  id: z.string().uuid(),

  /** Display name for the avatar companion */
  name: z.string().min(1).max(255),

  /** URL-friendly slug for the avatar companion */
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/),

  /** Detailed description of personality and expertise */
  description: z.string().nullable(),

  /** Path to the 3D model file (.glb) */
  modelPath: z.string().min(1).max(500),

  /** Admin-set room PIP framing, resolved from the avatar (`avatars.metadata.framing`). */
  framing: z.object({ fov: z.number(), heightOffset: z.number() }).nullable().optional(),
  /** The live version this body serves (#4632 phase 2), resolved with the model. */
  characterRelease: CharacterRuntimeReleaseSchema.nullable().optional(),
  /** Set only when the character was withdrawn and its assignments marked unavailable. */
  characterAvailability: CharacterUnavailabilitySchema.nullable().optional(),

  /** Cartesia voice ID for TTS */
  voiceId: z.string().min(1).max(255),

  /** Optional URL to video preview MP4 file (contains both video and audio) */
  videoPreviewUrl: z.string().max(500).nullable(),

  /** Thumbnail URL from joined avatar (for carousel display) */
  thumbnailUrl: z.string().max(500).nullable().optional(),

  /** Image preview URL from joined avatar (higher res than thumbnail) */
  imagePreviewUrl: z.string().max(500).nullable().optional(),

  /** Keywords used in LLM prompts for personality */
  promptKeywords: z.array(z.string()).nullable(),

  /** Category classification */
  category: z.nativeEnum(AvatarCompanionCategory),

  /** Tags for additional filtering and search */
  tags: z.array(z.string()).nullable(),

  /** Whether the companion is available for selection */
  isActive: z.boolean(),
  /**
   * #3672 — the platform default companion. A READ field only: it is
   * deliberately absent from `AvatarCompanionWriteSchema`'s pick list, because
   * it is a platform singleton guarded by a partial unique index rather than a
   * companion attribute. Promotion goes through `CompanionService.update`,
   * which clears the incumbent in the same transaction.
   */
  isDefault: z.boolean().optional(),

  /** Custom system prompt override (bypasses template generation if set) */
  customPrompt: z.string().nullable().optional(),
  /** #3666 — room mode entered on companion attach. Null = declares none. */
  defaultModeId: z.string().nullable().optional(),
  /** #3685 — skills enabled in a room on attach. Null = declares none. */
  skillIds: z.array(z.string()).nullable().optional(),

  /** Optional per-companion LLM configuration */
  brainConfig: z.record(z.any()).optional().nullable(),

  /** Record creation timestamp (ISO string) */
  createdAt: z.string(),

  /** Record last update timestamp (ISO string) */
  updatedAt: z.string()
});

/**
 * Schema for successful avatar companions API response
 */
export const AvatarCompanionsSuccessResponseSchema = z.object({
  /** Always true for success responses */
  success: z.literal(true),
  
  /** Array of avatar companions */
  data: z.array(AvatarCompanionAPISchema),
  
  /** Total count of companions returned */
  count: z.number().int().min(0)
});

/**
 * Schema for avatar companions API error response
 */
export const AvatarCompanionsErrorResponseSchema = z.object({
  /** Always false for error responses */
  success: z.literal(false),
  
  /** Error message for client display */
  error: z.string(),
  
  /** Optional error code for programmatic handling */
  code: z.string().optional()
});

/**
 * Schema for workflow avatar update request body
 */
export const WorkflowAvatarUpdateSchema = z.object({
  /** Avatar companion ID or null to remove assignment */
  avatarId: z.string().uuid().nullable()
});

/**
 * Schema for successful workflow avatar update response
 */
export const WorkflowAvatarSuccessResponseSchema = z.object({
  /** Always true for success responses */
  success: z.literal(true),
  
  /** Success message */
  message: z.string(),
  
  /** Updated workflow data */
  data: z.object({
    /** Updated workflow ID */
    workflowId: z.string().uuid(),
    
    /** Assigned avatar companion ID or null */
    avatarId: z.string().uuid().nullable(),
    
    /** Avatar companion details if assigned */
    avatarCompanion: z.object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
      description: z.string().nullable(),
      category: z.string(),
      modelPath: z.string(),
      voiceId: z.string()
    }).nullable().optional()
  })
});

/**
 * Schema for workflow avatar update error response
 */
export const WorkflowAvatarErrorResponseSchema = z.object({
  /** Always false for error responses */
  success: z.literal(false),
  
  /** Error message for client display */
  error: z.string(),
  
  /** Optional error code for programmatic handling */
  code: z.string().optional()
});

// =============================================================================
// API REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Query parameters for GET /api/avatar-companions
 */
export type GetAvatarCompanionsQuery = z.infer<typeof GetAvatarCompanionsQuerySchema>;

/**
 * Success response for GET /api/avatar-companions
 * 
 * Returns an array of active avatar companions with metadata
 * including total count for pagination support.
 */
export type AvatarCompanionsSuccessResponse = z.infer<typeof AvatarCompanionsSuccessResponseSchema>;

/**
 * Error response for avatar companions API
 * 
 * Standardized error response format with optional error codes
 * for programmatic error handling.
 */
export type AvatarCompanionsErrorResponse = z.infer<typeof AvatarCompanionsErrorResponseSchema>;

/**
 * Request body for PATCH /api/workflows/[workflowId]
 * 
 * Used to assign or remove avatar companions from workflows.
 * Setting avatarId to null removes the current assignment.
 */
export type WorkflowAvatarUpdate = z.infer<typeof WorkflowAvatarUpdateSchema>;

/**
 * Success response for workflow avatar update
 * 
 * Returns updated workflow data with avatar companion details
 * if an avatar was assigned.
 */
export type WorkflowAvatarSuccessResponse = z.infer<typeof WorkflowAvatarSuccessResponseSchema>;

/**
 * Error response for workflow avatar update
 * 
 * Includes specific error codes for different failure scenarios:
 * - INVALID_WORKFLOW_ID: Invalid UUID format
 * - WORKFLOW_NOT_FOUND: Workflow doesn't exist
 * - AVATAR_COMPANION_NOT_FOUND: Avatar companion doesn't exist or inactive
 * - RATE_LIMIT_EXCEEDED: Too many changes in short time
 * - DATABASE_ERROR: Database operation failed
 */
export type WorkflowAvatarErrorResponse = z.infer<typeof WorkflowAvatarErrorResponseSchema>;

// =============================================================================
// FRONTEND-SPECIFIC TYPES
// =============================================================================

/**
 * Avatar companion data optimized for API responses
 * 
 * Simplified version of AvatarCompanion with serialized dates
 * and optimized for JSON transmission to frontend clients.
 */
export type AvatarCompanionAPI = z.infer<typeof AvatarCompanionAPISchema>;

/**
 * Avatar companion selection item for UI components
 *
 * Minimal data required for avatar selection dropdowns,
 * lists, and cards in the frontend interface.
 */
export interface AvatarCompanionSelectionItem {
  /** Unique identifier */
  id: string;

  /** Display name */
  name: string;

  /** URL-friendly slug */
  slug: string;

  /** Brief description for tooltips */
  description: string | null;

  /** Category for grouping */
  category: string;

  /** 3D model path for preview */
  modelPath: string;
  /**
   * Admin-set room PIP framing, resolved from the avatar (`avatars.metadata.framing`). Absent
   * or null means the room's shipped composition.
   */
  framing?: { fov: number; heightOffset: number } | null;
  /** The live version this body serves (#4632 phase 2), resolved with the model. */
  characterRelease?: import('./AvatarManifest').CharacterRuntimeRelease | null;
  /** Set only when the character was withdrawn and its assignments marked unavailable. */
  characterAvailability?: import('./CharacterAvailability').CharacterUnavailability | null;

  /** Cartesia voice ID for TTS */
  voiceId: string;

  /** Video preview URL for testing (MP4 file with both video and audio) */
  videoPreviewUrl: string | null;

  /** Thumbnail URL from joined avatar (for carousel display - lightweight) */
  thumbnailUrl?: string | null;

  /** Image preview URL from joined avatar (higher res than thumbnail) */
  imagePreviewUrl?: string | null;

  /** Keywords used in LLM prompts for personality */
  promptKeywords: string[] | null;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Avatar companion with optional relations
 * 
 * Used in database queries that may include related workflow data
 */
export interface AvatarCompanionWithWorkflows extends AvatarCompanion {
  /** Related workflows using this avatar companion */
  workflows?: Array<{
    id: string;
    name: string;
    roomId: string | null;
    userId: string | null;
  }>;
}

/**
 * Avatar companion statistics
 * 
 * Used for admin dashboards and analytics
 */
export interface AvatarCompanionStats {
  /** Total number of companions */
  total: number;
  
  /** Number of active companions */
  active: number;
  
  /** Number of inactive companions */
  inactive: number;
  
  /** Count by category */
  byCategory: Record<string, number>;
  
  /** Most popular companions by usage */
  mostUsed: Array<{
    id: string;
    name: string;
    usageCount: number;
  }>;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if an object is a valid AvatarCompanion
 */
export function isAvatarCompanion(obj: unknown): obj is AvatarCompanion {
  return AvatarCompanionSchema.safeParse(obj).success;
}

/**
 * Type guard to check if a string is a valid avatar companion category
 */
export function isValidAvatarCompanionCategory(category: string): category is AvatarCompanionCategory {
  return AVATAR_COMPANION_CATEGORIES.includes(category as AvatarCompanionCategory);
}

/**
 * Type guard to check if an object is a valid success response
 */
export function isAvatarCompanionsSuccessResponse(obj: unknown): obj is AvatarCompanionsSuccessResponse {
  return AvatarCompanionsSuccessResponseSchema.safeParse(obj).success;
}

/**
 * Type guard to check if an object is a valid error response
 */
export function isAvatarCompanionsErrorResponse(obj: unknown): obj is AvatarCompanionsErrorResponse {
  return AvatarCompanionsErrorResponseSchema.safeParse(obj).success;
}

/**
 * Check a single companion row against the API read contract.
 *
 * `AvatarCompanionsSuccessResponseSchema.data` is a `z.array`, which is
 * all-or-nothing: one row the client cannot parse discards every other row with
 * it. Callers serving the catalog should screen rows individually with this and
 * drop (and log) the failures, so a defect in one record degrades to one
 * missing companion rather than an empty picker.
 *
 * Timestamps are accepted as `Date` objects (straight off Drizzle) as well as
 * ISO strings, since `AvatarCompanionAPISchema` models the post-JSON shape.
 * Unknown keys are ignored, so the caller can still return the full row.
 *
 * Returns the failing issues rather than throwing.
 */
export function checkAvatarCompanionApiContract(
  companion: unknown
): { valid: true } | { valid: false; issues: string[] } {
  const normalized =
    companion && typeof companion === 'object'
      ? (() => {
          const row = companion as Record<string, unknown>;
          return {
            ...row,
            createdAt: row.createdAt instanceof Date ? row.createdAt.toISOString() : row.createdAt,
            updatedAt: row.updatedAt instanceof Date ? row.updatedAt.toISOString() : row.updatedAt
          };
        })()
      : companion;

  const result = AvatarCompanionAPISchema.safeParse(normalized);
  if (result.success) {
    return { valid: true };
  }

  return {
    valid: false,
    issues: result.error.issues.map(
      issue => `${issue.path.join('.') || '(root)'}: ${issue.message}`
    )
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

// All types and schemas are exported above where they are defined
// No need for explicit re-export as TypeScript handles this automatically